import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isIncrementalKey, unlock as unlockPlayGamesAchievement } from "../lib/playGamesAchievements";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";
const TOAST_MAX_AGE_MS = 15 * 60 * 1000;

export type AchievementDef = {
  id: string;
  name: string;
  description: string;
  group_key: string;
  icon_key: string;
  sort_order: number;
};

export type EarnedAchievement = {
  achievement_id: string;
  earned_at: string;
  viewed_at: string | null;
};

export type AchievementToast = {
  id: string;
  name: string;
  icon_key: string;
  earned_at: string;
};

export type RecentAchievement = {
  achievement_id: string;
  name: string;
  icon_key: string;
  earned_at: string;
};

type UnlockContext = {
  match_id?: string | null;
  round?: number | null;
};

type AchievementsContextValue = {
  achievements: AchievementDef[];
  earned: EarnedAchievement[];
  hasNew: boolean;
  recentNew: RecentAchievement[];
  refresh: () => Promise<void>;
  markViewed: (opts: { achievementIds?: string[]; all?: boolean }) => Promise<void>;
  unlockAchievements: (achievementIds: string[], context?: UnlockContext) => Promise<{
    newly_unlocked: AchievementDef[];
    has_new: boolean;
  } | null>;
  toastQueue: AchievementToast[];
  dequeueToast: () => AchievementToast | null;
  requeueToast: (toast: AchievementToast) => void;
  safeToShow: boolean;
  setSafeToShow: (safe: boolean) => void;
  setMatchId: (matchId: string | null) => void;
};

const AchievementsContext = createContext<AchievementsContextValue | null>(null);

async function fetchWithAuth<T>(path: string, opts: RequestInit = {}): Promise<T | null> {
  // Check for regular user JWT token first (all platforms - JWT is primary)
  let regularUserToken: string | null = null;
  if (typeof window !== 'undefined') {
    regularUserToken = localStorage.getItem('regular_user_jwt');
  }
  
  // Check for guest token as fallback
  const { getValidGuestToken } = await import("../lib/guest-auth");
  const guestToken = await getValidGuestToken();
  
  const headers: HeadersInit = { "Content-Type": "application/json", ...(opts.headers || {}) };
  // Prefer regular user token over guest token
  if (regularUserToken) {
    headers["Authorization"] = `Bearer ${regularUserToken}`;
  } else if (guestToken) {
    headers["Authorization"] = `Bearer ${guestToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
    ...opts,
  });

  if (res.status === 401) {
    return null;
  }
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return res.json();
}

function toEarnedToast(definition: AchievementDef, earnedAt?: string): AchievementToast {
  return {
    id: definition.id,
    name: definition.name,
    icon_key: definition.icon_key,
    earned_at: earnedAt || new Date().toISOString(),
  };
}

export function AchievementsProvider({ children }: { children: React.ReactNode }) {
  const [achievements, setAchievements] = useState<AchievementDef[]>([]);
  const [earned, setEarned] = useState<EarnedAchievement[]>([]);
  const [hasNew, setHasNew] = useState(false);
  const [recentNew, setRecentNew] = useState<RecentAchievement[]>([]);
  const [toastQueue, setToastQueue] = useState<AchievementToast[]>([]);
  const [toastSlotsRemaining, setToastSlotsRemaining] = useState(3);
  const [safeToShow, setSafeToShow] = useState(true);
  const [matchId, setMatchId] = useState<string | null>(null);

  const toastQueueRef = useRef(toastQueue);
  const toastSlotsRef = useRef(toastSlotsRemaining);
  const matchIdRef = useRef<string | null>(matchId);
  const knownEarnedIdsRef = useRef<Set<string>>(new Set());
  const hydratedRef = useRef(false);

  useEffect(() => {
    toastQueueRef.current = toastQueue;
  }, [toastQueue]);

  useEffect(() => {
    toastSlotsRef.current = toastSlotsRemaining;
  }, [toastSlotsRemaining]);

  useEffect(() => {
    if (matchIdRef.current !== matchId) {
      matchIdRef.current = matchId;
      setToastSlotsRemaining(3);
    }
  }, [matchId]);

  const noteKnownEarnedIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const target = knownEarnedIdsRef.current;
    for (const id of ids) {
      target.add(id);
    }
  }, []);

  const markViewed = useCallback(
    async ({ achievementIds, all }: { achievementIds?: string[]; all?: boolean }) => {
      try {
        const payload = all ? { all: true } : { achievement_ids: achievementIds || [] };
        const data = await fetchWithAuth<{ updated: number; has_new: boolean }>(
          "/achievements/mark-viewed",
          {
            method: "POST",
            body: JSON.stringify(payload),
          }
        );

        if (!data) {
          return;
        }

        if (all) {
          const viewedAt = new Date().toISOString();
          setEarned((prev) =>
            prev.map((item) => ({ ...item, viewed_at: item.viewed_at || viewedAt }))
          );
        } else if (achievementIds && achievementIds.length > 0) {
          const viewedAt = new Date().toISOString();
          const ids = new Set(achievementIds);
          setEarned((prev) =>
            prev.map((item) =>
              ids.has(item.achievement_id)
                ? { ...item, viewed_at: item.viewed_at || viewedAt }
                : item
            )
          );
        }

        setHasNew(Boolean(data.has_new));
        if (all) {
          setRecentNew([]);
        } else if (achievementIds && achievementIds.length > 0) {
          const ids = new Set(achievementIds);
          setRecentNew((prev) => prev.filter((item) => !ids.has(item.achievement_id)));
        }
      } catch (err) {
        console.error("[Achievements] Failed to mark viewed:", err);
      }
    },
    []
  );

  const enqueueToasts = useCallback((defs: AchievementDef[]) => {
    if (defs.length === 0) return;
    const available = toastSlotsRef.current;
    if (available <= 0) return;

    const toAdd = defs
      .slice(0, available)
      .map((defn) => toEarnedToast(defn, (defn as any).earned_at));
    if (toAdd.length === 0) return;

    setToastQueue((prev) => [...prev, ...toAdd]);
    setToastSlotsRemaining((prev) => Math.max(0, prev - toAdd.length));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchWithAuth<{
        achievements: AchievementDef[];
        earned: EarnedAchievement[];
        has_new: boolean;
        recent_new: RecentAchievement[];
      }>("/achievements");

      if (!data) {
        setAchievements([]);
        setEarned([]);
        setHasNew(false);
        setRecentNew([]);
        knownEarnedIdsRef.current = new Set();
        hydratedRef.current = false;
        return;
      }

      const earnedRows = data.earned || [];
      setAchievements(data.achievements || []);
      setEarned(earnedRows);
      setHasNew(Boolean(data.has_new));
      setRecentNew(data.recent_new || []);

      const earnedIds = earnedRows.map((row) => row.achievement_id);
      if (!hydratedRef.current) {
        knownEarnedIdsRef.current = new Set(earnedIds);
        hydratedRef.current = true;
        return;
      }

      const newlyEarned = earnedRows.filter(
        (row) => !knownEarnedIdsRef.current.has(row.achievement_id)
      );
      if (newlyEarned.length === 0) {
        return;
      }

      const recentNew = data.recent_new || [];
      const recentById = new Map(
        recentNew.map((item) => [item.achievement_id, item])
      );
      const fallbackById = new Map(
        (data.achievements || []).map((item) => [item.id, item])
      );
      const toastDefs: AchievementDef[] = [];

      for (const row of newlyEarned) {
        const earnedAtMs = Date.parse(row.earned_at);
        if (!Number.isNaN(earnedAtMs)) {
          const ageMs = Date.now() - earnedAtMs;
          if (ageMs > TOAST_MAX_AGE_MS) {
            continue;
          }
        }
        const recent = recentById.get(row.achievement_id);
        const fallback = fallbackById.get(row.achievement_id);
        if (!fallback && !recent) continue;
        toastDefs.push({
          id: row.achievement_id,
          name: recent?.name || fallback?.name || row.achievement_id,
          description: fallback?.description || "",
          group_key: fallback?.group_key || "",
          icon_key: recent?.icon_key || fallback?.icon_key || "",
          sort_order: fallback?.sort_order ?? 0,
        });
      }

      if (toastDefs.length > 0) {
        toastDefs.sort((a, b) => a.sort_order - b.sort_order);
        enqueueToasts(toastDefs);
      }
      for (const row of newlyEarned) {
        if (!isIncrementalKey(row.achievement_id)) {
          void unlockPlayGamesAchievement(row.achievement_id);
        }
      }
      noteKnownEarnedIds(newlyEarned.map((row) => row.achievement_id));
    } catch (err) {
      console.error("[Achievements] Failed to refresh:", err);
    }
  }, [enqueueToasts, noteKnownEarnedIds]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const unlockAchievements = useCallback(
    async (achievementIds: string[], context?: UnlockContext) => {
      if (!achievementIds || achievementIds.length === 0) {
        return { newly_unlocked: [], has_new: hasNew };
      }

      try {
        const data = await fetchWithAuth<{
          newly_unlocked: AchievementDef[];
          has_new: boolean;
        }>("/achievements/unlock", {
          method: "POST",
          body: JSON.stringify({
            achievement_ids: achievementIds,
            context: context || undefined,
          }),
        });

        if (!data) {
          return null;
        }

        const newlyUnlocked = data.newly_unlocked || [];
        if (newlyUnlocked.length > 0) {
          noteKnownEarnedIds(newlyUnlocked.map((defn) => defn.id));
          setEarned((prev) => {
            const existingIds = new Set(prev.map((item) => item.achievement_id));
            const next = [...prev];
            const earnedAt = new Date().toISOString();
            for (const defn of newlyUnlocked) {
              if (!existingIds.has(defn.id)) {
                next.unshift({
                  achievement_id: defn.id,
                  earned_at: (defn as any).earned_at || earnedAt,
                  viewed_at: null,
                });
              }
            }
            return next;
          });
          const earnedAt = new Date().toISOString();
          setRecentNew((prev) => {
            const existing = new Set(prev.map((item) => item.achievement_id));
            const additions = newlyUnlocked
              .filter((defn) => !existing.has(defn.id))
              .map((defn) => ({
                achievement_id: defn.id,
                name: defn.name,
                icon_key: defn.icon_key,
                earned_at: (defn as any).earned_at || earnedAt,
              }));
            return [...additions, ...prev].slice(0, 5);
          });
          enqueueToasts(newlyUnlocked);
          for (const defn of newlyUnlocked) {
            if (!isIncrementalKey(defn.id)) {
              void unlockPlayGamesAchievement(defn.id);
            }
          }
        }

        setHasNew(Boolean(data.has_new));
        return data;
      } catch (err) {
        console.error("[Achievements] Failed to unlock:", err);
        return null;
      }
    },
    [enqueueToasts, hasNew]
  );

  const dequeueToast = useCallback(() => {
    const queue = toastQueueRef.current;
    if (queue.length === 0) return null;
    const [next, ...rest] = queue;
    setToastQueue(rest);
    return next;
  }, []);

  const requeueToast = useCallback((toast: AchievementToast) => {
    setToastQueue((prev) => [toast, ...prev]);
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;

    (window as any).__debugUnlockAchievement = (ids: string[] | string) => {
      const list = Array.isArray(ids) ? ids : [ids];
      return unlockAchievements(list, { match_id: matchIdRef.current || undefined });
    };

    return () => {
      delete (window as any).__debugUnlockAchievement;
    };
  }, [unlockAchievements]);

  const value = useMemo(
    () => ({
      achievements,
      earned,
      hasNew,
      recentNew,
      refresh,
      markViewed,
      unlockAchievements,
      toastQueue,
      dequeueToast,
      requeueToast,
      safeToShow,
      setSafeToShow,
      setMatchId,
    }),
    [
      achievements,
      earned,
      hasNew,
      recentNew,
      refresh,
      markViewed,
      unlockAchievements,
      toastQueue,
      dequeueToast,
      requeueToast,
      safeToShow,
      setSafeToShow,
      setMatchId,
    ]
  );

  return <AchievementsContext.Provider value={value}>{children}</AchievementsContext.Provider>;
}

export function useAchievements() {
  const context = useContext(AchievementsContext);
  if (!context) {
    throw new Error("useAchievements must be used within AchievementsProvider");
  }
  return context;
}
